import { STATUS_META } from '@/lib/journey'
import type { ProcedureStatus } from '@/types/cgt'

/**
 * Presentation for a procedure's status, shared by the patients list and the
 * patient detail page so the same procedure never renders two different labels.
 *
 * `ProcedureStatus` includes `cancelled`, which `STATUS_META` (keyed by the
 * journey-step status) has no entry for. Typing this as a full Record keeps it
 * exhaustive: TypeScript errors if `ProcedureStatus` grows a value this map does
 * not cover, instead of silently mislabelling it at runtime.
 */
export const PROCEDURE_STATUS_META: Record<ProcedureStatus, (typeof STATUS_META)['completed']> = {
  completed: STATUS_META.completed,
  in_progress: STATUS_META.in_progress,
  cancelled: {
    label: 'Cancelled',
    dot: 'bg-red-400',
    text: 'text-red-600',
    badge: 'bg-red-50 text-red-600 border-red-200',
  },
}

/**
 * The status chip for a procedure: its own status, except that a run with a
 * form waiting for its reviews (z8nrz7dt41) reads "Pending review" in yellow —
 * the same precedence `deriveProcedureStepStatus` gives the procedure tables.
 */
export function procedureStatusMeta(p: {
  status: ProcedureStatus
  hasPendingReview?: boolean
}): (typeof STATUS_META)['completed'] {
  if (p.hasPendingReview && p.status !== 'cancelled') return STATUS_META.pending_review
  return PROCEDURE_STATUS_META[p.status]
}

/**
 * A patient's procedures for a one-line cell (the Patients list): the latest
 * started first, then the rest newest first. Ties keep the order given.
 */
export function latestProcedureFirst<T extends { startedAt: string }>(procedures: readonly T[]): { latest: T | null; rest: T[] } {
  const sorted = procedures
    .map((p, i) => ({ p, i, t: Date.parse(p.startedAt) }))
    .sort((a, b) => (Number.isNaN(b.t) ? -Infinity : b.t) - (Number.isNaN(a.t) ? -Infinity : a.t) || a.i - b.i)
    .map(({ p }) => p)
  return { latest: sorted[0] ?? null, rest: sorted.slice(1) }
}
